import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { TID } from "@/lib/testIds";
import { ArrowRight, CheckSquare, ShieldCheck, Sparkle, ChatCircleText, ArrowUpRight, MagnifyingGlass, LockKey, Certificate, Briefcase, GlobeHemisphereWest, Rocket, Target, Megaphone, Handshake, Confetti, Bank, CalendarX, Coin, Eye } from "@phosphor-icons/react";
import Footer from "@/components/layout/Footer";
import Navbar from "@/components/layout/Navbar";
import SEO from "@/components/SEO";

const EXAMPLES = [
  "IT Directors and above at manufacturing companies in the United States with 500 to 5,000 employees that use SAP.",
  "VPs of Marketing at Series B–D SaaS companies in Germany, France and UK (50–500 employees).",
  "Heads of Procurement at hospitals in the US with more than 1,000 beds.",
  "CFOs and Finance Directors at APAC fintechs in Singapore, Australia and Japan.",
];

const FAQS = [
  { q: "Is a subscription required?", a: "No. ExactReach is pay-per-project. No monthly minimum, no expiring credits, no annual commitment." },
  { q: "When do I pay?", a: "Only after you have reviewed the audience estimate, the 20-row sample and approved a quote option." },
  { q: "What is Verified Data?", a: "Records match your targeting criteria and every business email is freshly re-verified close to fulfilment. Invalid emails are removed or replaced according to the order terms." },
  { q: "Can I upload suppression lists?", a: "Yes. Upload a CSV or XLSX file with your existing accounts or contacts to exclude." },
  { q: "Can a previous audience be reordered?", a: "Yes. Duplicate a previous request from your dashboard to reuse the criteria." },
];

export default function Home() {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryBoxRef = useRef(null);
  const textareaRef = useRef(null);

  // Typewriter placeholder — cycles through EXAMPLES in random order, char by char.
  const [animPh, setAnimPh] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const animRef = useRef({ order: [], idx: 0, pos: 0, mode: "typing", timer: null });
  const queryLenRef = useRef(0);
  const focusedRef = useRef(false);
  useEffect(() => { queryLenRef.current = query.length; }, [query]);
  useEffect(() => { focusedRef.current = isFocused; }, [isFocused]);

  useEffect(() => {
    const shuffle = (arr) => {
      const a = [...arr];
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    };
    animRef.current.order = shuffle(EXAMPLES);
    animRef.current.idx = 0;
    animRef.current.pos = 0;
    animRef.current.mode = "typing";

    const TYPING_MS = 32;      // per character while typing
    const DELETE_MS = 14;      // per character while deleting
    const HOLD_FULL_MS = 1600; // pause once fully typed
    const HOLD_EMPTY_MS = 300; // pause between examples

    const tick = () => {
      // Pause when the user is interacting with the box.
      if (focusedRef.current || queryLenRef.current > 0) {
        animRef.current.timer = setTimeout(tick, 400);
        return;
      }
      const s = animRef.current;
      const current = s.order[s.idx % s.order.length];
      if (s.mode === "typing") {
        s.pos += 1;
        setAnimPh(current.slice(0, s.pos));
        if (s.pos >= current.length) {
          s.mode = "hold_full";
          animRef.current.timer = setTimeout(tick, HOLD_FULL_MS);
          return;
        }
        animRef.current.timer = setTimeout(tick, TYPING_MS);
      } else if (s.mode === "hold_full") {
        s.mode = "deleting";
        animRef.current.timer = setTimeout(tick, DELETE_MS);
      } else if (s.mode === "deleting") {
        s.pos -= 1;
        setAnimPh(current.slice(0, Math.max(0, s.pos)));
        if (s.pos <= 0) {
          s.pos = 0;
          s.idx += 1;
          s.mode = "typing";
          // Re-shuffle at the end of each full pass for fresh order.
          if (s.idx >= s.order.length) {
            s.order = shuffle(EXAMPLES);
            s.idx = 0;
          }
          animRef.current.timer = setTimeout(tick, HOLD_EMPTY_MS);
          return;
        }
        animRef.current.timer = setTimeout(tick, DELETE_MS);
      }
    };
    animRef.current.timer = setTimeout(tick, 400);
    return () => { if (animRef.current.timer) clearTimeout(animRef.current.timer); };
  }, []);

  const scrollToQuery = () => {
    if (queryBoxRef.current) {
      queryBoxRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => textareaRef.current?.focus(), 500);
    }
  };

  // Fire on mount if arriving with #query or ?scroll=query, and on hash change while on this page
  useEffect(() => {
    const wants = window.location.hash === "#query" || searchParams.get("scroll") === "query";
    if (wants) {
      setTimeout(scrollToQuery, 100);
    }
    const onHash = () => {
      if (window.location.hash === "#query") scrollToQuery();
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [searchParams]);

  const startRequest = () => {
    try { sessionStorage.setItem("er_query", query); } catch (e) { console.warn("[Home] sessionStorage blocked:", e); }
    navigate("/new-request");
  };

  return (
    <div className="App min-h-screen">
      <SEO />
      <Navbar />

      {/* HERO */}
      <section id="hero" data-testid={TID.home.hero} className="border-b border-border relative">
        <div className="absolute inset-0 grid-lines opacity-[0.35] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-24 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 anim-fade-up">
            <div className="flex flex-wrap gap-2 md:gap-2.5 mb-8" data-testid="home-promises">
              <div className="inline-flex items-center gap-2 pl-2.5 pr-4 py-2 rounded-full bg-primary text-primary-foreground shadow-sm anim-fade-up stagger-1" data-testid="promise-0">
                <span className="h-6 w-6 grid place-items-center rounded-full bg-white/20">
                  <CalendarX size={14} weight="bold" />
                </span>
                <span className="font-semibold text-sm tracking-tight">No annual commitment</span>
              </div>
              <div className="inline-flex items-center gap-2 pl-2.5 pr-4 py-2 rounded-full bg-emerald-600 text-white shadow-sm anim-fade-up stagger-2" data-testid="promise-1">
                <span className="h-6 w-6 grid place-items-center rounded-full bg-white/20">
                  <Coin size={14} weight="bold" />
                </span>
                <span className="font-semibold text-sm tracking-tight">No expiring credits</span>
              </div>
              <div className="inline-flex items-center gap-2 pl-2.5 pr-4 py-2 rounded-full bg-amber-500 text-white shadow-sm anim-fade-up stagger-3" data-testid="promise-2">
                <span className="h-6 w-6 grid place-items-center rounded-full bg-white/25">
                  <Eye size={14} weight="bold" />
                </span>
                <span className="font-semibold text-sm tracking-tight">Sample before you pay</span>
              </div>
            </div>
            <h1 className="font-heading font-black tracking-tighter text-5xl md:text-6xl lg:text-7xl leading-[0.95] mb-6">
              Tell us who you want<br /> to reach. <span className="text-muted-foreground">We do the rest.</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mb-8 leading-relaxed">
              Describe your audience in <span className="bg-gradient-to-r from-fuchsia-600 via-rose-500 to-orange-500 bg-clip-text text-transparent font-bold">one messy sentence</span>. Check the estimated availability. Review a 20-record sample. Pay only for the verified records you actually need — <span className="bg-gradient-to-r from-fuchsia-600 via-rose-500 to-orange-500 bg-clip-text text-transparent font-bold">no seat licenses, no "contact us" games</span>.
            </p>
          </div>

          <div id="query" ref={queryBoxRef} className="lg:col-span-5 lg:pt-4 anim-fade-up stagger-2 scroll-mt-24">
            <div className="border-2 border-foreground bg-white p-6 offset-shadow-primary">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs font-mono tracking-widest-2 uppercase font-semibold">Check My Audience</div>
                <div className="text-[10px] font-mono text-muted-foreground">FREE · NO ACCOUNT NEEDED YET</div>
              </div>
              <Textarea
                ref={textareaRef}
                data-testid={TID.home.queryBox}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                placeholder={animPh || EXAMPLES[0]}
                rows={5}
                className="border-border font-mono text-sm resize-none focus-visible:ring-primary placeholder:text-muted-foreground/60"
              />
              <Button
                data-testid={TID.home.checkBtn}
                onClick={startRequest}
                className="w-full mt-3 rounded-sm h-11 text-sm font-semibold group"
              >
                Start my audience request
                <ArrowRight size={16} className="ml-2 group-hover:translate-x-0.5 transition-transform" />
              </Button>
              <div className="mt-4">
                <div className="text-[10px] font-mono uppercase tracking-widest-2 text-muted-foreground mb-2">Examples</div>
                <div className="flex flex-wrap gap-2">
                  {EXAMPLES.slice(0, 3).map((e, i) => (
                    <button
                      key={e}
                      data-testid={`${TID.home.exampleChip}-${i}`}
                      onClick={() => setQuery(e)}
                      className="text-[11px] text-left px-2.5 py-1.5 border border-border hover:border-foreground hover:bg-muted rounded-sm max-w-full truncate"
                      title={e}
                    >
                      {e.length > 60 ? e.slice(0, 60) + "…" : e}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3 font-mono">
              This does not run a live database search. Our team reviews every request.
            </p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-14">
            <div>
              <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-3">Process</div>
              <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tighter max-w-xl">
                Four steps. No surprises.
              </h2>
            </div>
            <Link to="/how-it-works" className="hidden md:inline-flex items-center gap-1 text-sm font-medium hover:underline">
              Full walkthrough <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
            {[
              { t: "Describe your audience", d: "Plain English or structured filters.", icon: ChatCircleText },
              { t: "Receive an estimate + sample", d: "Volume, 20-row sample, delivery ETA.", icon: MagnifyingGlass },
              { t: "Approve & pay", d: "Only after you're happy with the estimate.", icon: LockKey },
              { t: "Download securely", d: "Encrypted, time-limited download links.", icon: Certificate },
            ].map((s) => (
              <div key={s.t} className="bg-white p-6 flex flex-col">
                <s.icon size={26} weight="duotone" className="mb-4 text-primary" />
                <div className="font-semibold text-base leading-tight mb-1">{s.t}</div>
                <div className="text-xs text-muted-foreground leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO THIS IS FOR · Use cases */}
      <section className="section border-b border-border bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-end justify-between mb-14 flex-wrap gap-6">
            <div>
              <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-3">Who this is for</div>
              <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tighter max-w-2xl">
                Built for teams that need <span className="text-muted-foreground">the exact audience</span>, not a whole database.
              </h2>
              <p className="text-[15px] text-muted-foreground mt-4 max-w-2xl leading-[1.65]">
                <span className="bg-gradient-to-r from-fuchsia-600 via-rose-500 to-orange-500 bg-clip-text text-transparent font-bold">If you know the audience but don't want to marry a data platform for the year to reach them</span>, ExactReach is designed for you.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border" data-testid="home-use-cases">
            {[
              {
                icon: Target,
                t: "Account-based outbound",
                d: "You have a defined ICP and want the buying committee inside a curated account list — no more than N contacts per company, direct dials only.",
              },
              {
                icon: GlobeHemisphereWest,
                t: "Testing a new market",
                d: "Expanding into a country, region or vertical for the first time and need a clean list to validate demand before you commit budget.",
              },
              {
                icon: Rocket,
                t: "Product launch",
                d: "You're releasing a new product or category and need net-new prospects who fit the launch narrative — fast, without a subscription.",
              },
              {
                icon: Briefcase,
                t: "Recruiting a niche audience",
                d: "Hiring for a specific role, seniority or skill set (or targeting passive candidates) and need names + verified emails to run outreach.",
              },
              {
                icon: Megaphone,
                t: "Event & webinar invites",
                d: "Filling a launch event, roadshow or webinar with the right seats — filtered by title, industry, city and company size.",
              },
              {
                icon: Handshake,
                t: "Partner & channel recruitment",
                d: "Building a partner, reseller or affiliate programme and need to reach the exact decision-makers in agencies, integrators or resellers.",
              },
              {
                icon: Bank,
                t: "Fundraising & investor outreach",
                d: "Targeting VCs, family offices or corporate development contacts that fit your stage, geography and thesis — no bloated CRM required.",
              },
              {
                icon: Confetti,
                t: "One-off campaigns",
                d: "You run 3–4 campaigns a year, not 3–4 hundred. Pay per list, keep the data, walk away with no ongoing bill.",
              },
            ].map((u) => (
              <div key={u.t} className="bg-white p-6 flex flex-col" data-testid={`use-case-${u.t.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <u.icon size={26} weight="duotone" className="mb-4 text-primary" />
                <div className="font-semibold text-base leading-tight mb-2">{u.t}</div>
                <div className="text-[13px] text-muted-foreground leading-relaxed">{u.d}</div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-sm text-muted-foreground max-w-3xl leading-[1.65]">
            Not seeing your use case? If your audience can be described in a sentence, we can probably source it.{" "}
            <Link to="/#query" className="text-foreground underline font-medium" data-testid="use-cases-check-link">
              Describe your audience →
            </Link>
          </div>
        </div>
      </section>

      {/* VERIFIED DATA · What you get */}
      <section className="section border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-14">
            <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-3">Data quality</div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tighter">Only verified data. No shortcuts.</h2>
            <p className="text-[15px] text-muted-foreground mt-4 max-w-2xl leading-[1.65]">
              Every ExactReach list is delivered as verified data. <span className="bg-gradient-to-r from-fuchsia-600 via-rose-500 to-orange-500 bg-clip-text text-transparent font-bold">We don't sell stale records or ancient conference exports</span> — every business email is re-checked close to fulfilment, and firmographic fields are validated against multiple sources.
            </p>
          </div>

          <div className="relative border-2 border-primary bg-white p-8 md:p-10 offset-shadow-primary max-w-3xl">
            <div className="absolute -top-3 left-6 bg-primary text-primary-foreground text-[10px] font-mono uppercase tracking-widest-2 font-bold px-2 py-1">Our standard</div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-xs font-mono uppercase text-primary tracking-widest-2 mb-1">What you get</div>
                <h3 className="text-2xl md:text-3xl font-bold font-heading">Verified Data</h3>
              </div>
              <ShieldCheck size={32} weight="duotone" className="text-primary" />
            </div>
            <ul className="grid md:grid-cols-2 gap-3 text-sm">
              {[
                "Matches your exact targeting criteria",
                "Business emails re-verified close to fulfilment",
                "Firmographic fields validated against multiple sources",
                "Invalid emails removed or replaced within tolerance",
                "Optional phone verification and human review available",
              ].map((x) => (
                <li key={x} className="flex gap-2"><CheckSquare weight="fill" size={16} className="mt-0.5 text-primary shrink-0" />{x}</li>
              ))}
            </ul>
          </div>

          <p className="text-xs text-muted-foreground mt-6 font-mono">
            Phone verification, direct dial validation and human review may be quoted separately.
          </p>
        </div>
      </section>

      {/* SOCIAL PROOF · Stats + logos */}
      <section className="border-b border-border py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-3">Trusted by revenue teams worldwide</div>
            <h2 className="font-heading font-bold tracking-tighter text-3xl md:text-5xl max-w-3xl mx-auto">
              Purpose-built for B2B teams who <span className="text-muted-foreground">move fast.</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border mb-16" data-testid="home-stats">
            {[
              { n: "10,400+", l: "Lists delivered" },
              { n: "38M+", l: "Verified records shipped" },
              { n: "97%", l: "Deliverability on Verified" },
              { n: "68%", l: "Customers reorder within 120 days" },
            ].map((s, i) => (
              <div key={i} className="bg-white p-6 md:p-8 text-center">
                <div className="font-heading font-black tracking-tighter text-4xl md:text-5xl text-primary">{s.n}</div>
                <div className="text-xs uppercase tracking-widest-2 text-muted-foreground font-semibold mt-2">{s.l}</div>
              </div>
            ))}
          </div>

          <div className="text-[10px] uppercase tracking-widest-2 text-muted-foreground font-semibold text-center mb-6">Teams using ExactReach</div>
          {(() => {
            const LOGOS = [
              // — Placeholder demo names (Microsoft canon)
              { n: "NORTHWIND", w: "font-heading font-black tracking-[0.02em]" },
              { n: "Contoso.", w: "font-heading font-bold italic tracking-tight" },
              { n: "FABRIKAM", w: "font-mono font-bold tracking-[0.15em] text-xs" },
              { n: "WINGTIP", w: "font-heading font-extrabold" },
              { n: "adventure /works", w: "font-heading font-light tracking-tight" },
              { n: "LITWARE", w: "font-heading font-black tracking-tighter" },
              { n: "Tailspin", w: "font-heading font-semibold" },
              { n: "PROSEWARE", w: "font-mono tracking-widest text-xs font-bold" },
              { n: "Relecloud", w: "font-heading font-bold italic" },
              { n: "VanArsdel", w: "font-heading font-black" },
              { n: "Trey Research", w: "font-heading font-medium tracking-tight" },
              { n: "COHO", w: "font-heading font-black tracking-[0.3em]" },
              // — SaaS placeholders · 200-500 emp
              { n: "loomstack", w: "font-heading font-semibold tracking-tight lowercase" },
              { n: "Cadenzo", w: "font-heading font-bold italic tracking-tight" },
              { n: "prismforge", w: "font-heading font-black lowercase tracking-tight" },
              { n: "Runwell", w: "font-heading font-semibold tracking-tight" },
              { n: "THREADPATH", w: "font-mono font-bold tracking-[0.18em] text-xs" },
              { n: "Byteroom", w: "font-heading font-bold" },
              { n: "slateflow.", w: "font-heading font-black lowercase tracking-tight" },
              { n: "Signalcove", w: "font-heading font-medium italic tracking-tight" },
              { n: "Metergrid", w: "font-heading font-black tracking-tighter" },
              { n: "kettlepoint", w: "font-heading font-light lowercase tracking-tight" },
              // — SaaS placeholders · 500-1000 emp
              { n: "NORTHLINK", w: "font-heading font-black tracking-[0.05em]" },
              { n: "Voltcanvas", w: "font-heading font-bold" },
              { n: "Riverbench", w: "font-heading font-semibold italic" },
              { n: "PARALLELFORGE", w: "font-mono font-bold tracking-widest text-xs" },
              { n: "cinderworks", w: "font-heading font-black lowercase tracking-tight" },
              // — SaaS placeholders · 1000+ emp
              { n: "MERIDIANSTACK", w: "font-heading font-black tracking-[0.02em]" },
              { n: "Blockyard.", w: "font-heading font-bold italic tracking-tight" },
              { n: "Copperloom", w: "font-heading font-extrabold tracking-tight" },
            ];
            const doubled = [...LOGOS, ...LOGOS];
            return (
              <div className="marquee marquee-fade" data-testid="home-logos">
                <div className="marquee-track" style={{ animationDuration: "96s" }}>
                  {doubled.map((c, i) => (
                    <div
                      key={`${c.n}-${i}`}
                      aria-hidden={i >= LOGOS.length ? "true" : undefined}
                      className={`px-8 md:px-12 text-muted-foreground hover:text-foreground transition-colors text-lg md:text-xl whitespace-nowrap ${c.w}`}
                    >
                      {c.n}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="border-b border-border py-16 md:py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-12">
            <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-3">Customer stories</div>
            <h2 className="font-heading font-bold tracking-tighter text-3xl md:text-5xl max-w-2xl">
              Real teams. Real lists. <span className="text-muted-foreground">Zero waste.</span>
            </h2>
          </div>
          {(() => {
            const TESTIMONIALS = [
              { q: "We stopped paying for a seat-based platform we barely used. ExactReach turned prospecting into a line item — 4 requests this quarter, exactly the audiences we needed.", a: "Priya S.", r: "VP Demand Gen", c: "Northwind Traders" },
              { q: "The 20-row sample changed everything. We caught two targeting misreads before spending a dollar. Verified deliverability came back at 96% on the first list.", a: "Marco A.", r: "Head of Sales Ops", c: "Contoso Ltd." },
              { q: "For our EMEA launch we needed CFOs across 11 countries. Quoted in 6 hours, delivered in 3 days. Verified deliverability at 97%. Doing it again next quarter.", a: "Sofia M.", r: "Director, Field Marketing", c: "Fabrikam Inc." },
              { q: "Tried three data platforms before this. ExactReach was the only one that got our niche vertical right on the first sample. No credit hoarding, no seat sprawl.", a: "Daniel K.", r: "Growth Lead", c: "Wingtip Toys" },
              { q: "We run four campaigns a year, not four hundred. Paying per list instead of per year cut our data spend by 62% with better deliverability.", a: "Aisha R.", r: "CMO", c: "Litware Inc." },
              { q: "Human research on our niche healthcare list saved us weeks. The final file matched the brief line by line. Rare in this category.", a: "Tomás B.", r: "Head of Partnerships", c: "Relecloud" },
              { q: "Fast, precise, and no upsell games. The team flagged a targeting gap we hadn't seen and re-issued the estimate in an hour.", a: "Hannah L.", r: "RevOps Manager", c: "Tailspin" },
              { q: "Bought a 12-country CFO list, delivered clean, replaced the bounces without arguing. That alone justifies switching from our old vendor.", a: "Jae-min C.", r: "APAC Sales Director", c: "Trey Research" },
            ];
            const doubled = [...TESTIMONIALS, ...TESTIMONIALS];
            return (
              <div className="marquee marquee-fade" data-testid="home-testimonials">
                <div className="marquee-track gap-6 py-2" style={{ animationDuration: "80s" }}>
                  {doubled.map((t, i) => (
                    <div
                      key={`${t.a}-${t.c}-${i}`}
                      aria-hidden={i >= TESTIMONIALS.length ? "true" : undefined}
                      className="bg-white border border-border p-6 md:p-8 w-[340px] md:w-[400px]"
                    >
                      <div className="text-3xl leading-none font-heading font-black text-primary mb-4">"</div>
                      <p className="text-base leading-relaxed mb-6">{t.q}</p>
                      <div className="pt-4 border-t border-border">
                        <div className="font-semibold text-sm">{t.a}</div>
                        <div className="text-xs text-muted-foreground">{t.r} · {t.c}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* FAQ */}
      <section className="section border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="text-xs tracking-widest-2 uppercase text-muted-foreground font-semibold mb-3">Common questions</div>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tighter">The short answers.</h2>
            <Link to="/faq" className="mt-6 inline-flex items-center gap-1 text-sm font-medium hover:underline">
              See all FAQs <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="lg:col-span-8">
            {FAQS.map((f, i) => (
              <details key={f.q} data-testid={`${TID.home.faqItem}-${i}`} className="group border-b border-border py-5">
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="font-semibold text-base pr-4">{f.q}</span>
                  <span className="text-2xl leading-none text-muted-foreground group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section" data-testid={TID.home.cta}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <Sparkle size={28} weight="duotone" className="mx-auto mb-6 text-primary" />
          <h2 className="font-heading font-black tracking-tighter text-4xl md:text-6xl mb-6">Ready to start?</h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
            Describe your audience <span className="bg-gradient-to-r from-fuchsia-600 via-rose-500 to-orange-500 bg-clip-text text-transparent font-bold">in one honest sentence</span>. See what's possible before you spend a cent.
          </p>
          <Link to="/#query">
            <Button size="lg" className="rounded-sm h-12 px-6" data-testid="home-cta-btn">
              Check my audience
              <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
